import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '../../store/uiStore';
import { useLanguageStore } from '../../store/languageStore';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  FileText, 
  Settings, 
  Pill,
  FlaskConical,
  Scan,
  X,
  ChevronRight,
  ChevronLeft,
  Stethoscope
} from 'lucide-react';

// Menu items configuration
const menuItems = [
  { path: '/', key: 'dashboard', icon: LayoutDashboard },
  { path: '/patients', key: 'patients', icon: Users },
  { path: '/visits/new', key: 'newVisit', icon: Calendar },
  { path: '/prescriptions/new', key: 'newPrescription', icon: FileText },
  { path: '/drugs', key: 'drugs', icon: Pill },
  { path: '/lab-tests', key: 'labTests', icon: FlaskConical },
  { path: '/radiology-tests', key: 'radiologyTests', icon: Scan },
  { path: '/settings/prescription', key: 'settings', icon: Settings },
];


// Sidebar Header Component
interface SidebarHeaderProps {
  isCollapsed: boolean;
  isRTL: boolean;
  t: (key: string) => string;
}

const SidebarHeader: React.FC<SidebarHeaderProps> = ({ isCollapsed, isRTL, t }) => {
  return (
    <motion.div
      className="position-relative"
      style={{
        padding: '1.5rem',
        paddingBlockEnd: '1.25rem',
        paddingInlineStart: '1.5rem',
        paddingInlineEnd: '1.5rem',
        borderBlockEnd: '1px solid rgba(255, 255, 255, 0.1)',
        minHeight: '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : (isRTL ? 'flex-end' : 'flex-start'),
      }}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {!isCollapsed && (
        <motion.div
          className="d-flex align-items-center"
          style={{
            flexDirection: isRTL ? 'row-reverse' : 'row',
            width: '100%',
            gap: '0.75rem',
          }}
          initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          {/* Logo/Icon Circle */}
          <motion.div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: '48px',
              height: '48px',
              flexShrink: 0,
              background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.3), rgba(59, 130, 246, 0.3))',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
            whileHover={{ scale: 1.05, rotate: 5 }}
            transition={{ duration: 0.2 }}
          >
            <Stethoscope size={24} style={{ color: 'rgba(255, 255, 255, 0.95)' }} />
          </motion.div>

          {/* App Name and Subtitle */}
          <div 
            className="d-flex flex-column"
            style={{
              textAlign: isRTL ? 'right' : 'left',
              flex: 1,
            }}
          >
            <motion.h4
              className="mb-0 fw-bold"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.9) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: '1.25rem',
                letterSpacing: isRTL ? '0' : '0.02em',
                margin: 0,
                lineHeight: 1.2,
                direction: isRTL ? 'rtl' : 'ltr',
              }}
            >
              {t('common.clinicName')}
            </motion.h4>
            <motion.span
              className="small"
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.75rem',
                marginTop: '2px',
                direction: isRTL ? 'rtl' : 'ltr',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {t('common.adminPanel')}
            </motion.span>
          </div>
        </motion.div>
      )}

      {isCollapsed && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
        >
          <Stethoscope size={28} style={{ color: 'rgba(255, 255, 255, 0.95)' }} />
        </motion.div>
      )}
    </motion.div>
  );
};

// Sidebar Item Component
interface SidebarItemProps {
  item: typeof menuItems[0];
  isActive: boolean;
  isCollapsed: boolean;
  isRTL: boolean;
  t: (key: string) => string;
  index: number;
  onNavigate: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ 
  item, 
  isActive, 
  isCollapsed, 
  isRTL, 
  t, 
  index,
  onNavigate 
}) => {
  const Icon = item.icon;
  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <motion.li
      style={{
        marginBlockEnd: '0.5rem',
        listStyle: 'none',
      }}
      initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.1 + index * 0.03 }}
    >
      <Link
        to={item.path}
        onClick={onNavigate}
        className="text-decoration-none"
        style={{ display: 'block' }}
        aria-label={t(`common.${item.key}`)}
      >
        <motion.div
          className="position-relative"
          style={{
            padding: isCollapsed ? '0.875rem' : '0.875rem 1rem',
            paddingInlineStart: isCollapsed ? '0.875rem' : '1rem',
            paddingInlineEnd: isCollapsed ? '0.875rem' : '1rem',
            borderRadius: '12px',
            minHeight: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : (isRTL ? 'flex-end' : 'flex-start'),
            gap: '0.75rem',
            background: isActive 
              ? 'linear-gradient(135deg, rgba(96, 165, 250, 0.25), rgba(59, 130, 246, 0.2))'
              : 'rgba(255, 255, 255, 0.05)',
            border: isActive 
              ? '1px solid rgba(96, 165, 250, 0.3)'
              : '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: isActive
              ? '0 4px 12px rgba(96, 165, 250, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              : '0 2px 4px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            flexDirection: 'row',
            direction: isRTL ? 'rtl' : 'ltr',
            transition: 'all 0.2s ease',
          }}
          whileHover={{
            background: isActive 
              ? 'linear-gradient(135deg, rgba(96, 165, 250, 0.3), rgba(59, 130, 246, 0.25))'
              : 'rgba(255, 255, 255, 0.1)',
            borderColor: isActive 
              ? 'rgba(96, 165, 250, 0.4)'
              : 'rgba(255, 255, 255, 0.2)',
            boxShadow: isActive
              ? '0 6px 16px rgba(96, 165, 250, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
              : '0 4px 8px rgba(0, 0, 0, 0.15)',
            y: -1,
            scale: 1.02,
          }}
          whileTap={{ scale: 0.98 }}
        >
          {/* Active Indicator Bar */}
          {isActive && (
            <motion.div
              layoutId="activeIndicator"
              className="position-absolute"
              style={{
                [isRTL ? 'right' : 'left']: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: '4px',
                height: '60%',
                background: 'linear-gradient(to bottom, #ffffff, rgba(255, 255, 255, 0.8))',
                borderRadius: isRTL ? '0 4px 4px 0' : '4px 0 0 4px',
                boxShadow: '0 0 12px rgba(255, 255, 255, 0.5), 0 0 24px rgba(96, 165, 250, 0.4)',
              }}
              initial={false}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          )}

          {isRTL && !isCollapsed ? (
            <>
              {/* RTL Layout: Icon (left) → Text → Chevron (right) */}
              {/* Icon - على اليسار */}
              <motion.div
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  order: 1,
                }}
                animate={{
                  scale: isActive ? 1.1 : 1,
                }}
                transition={{ duration: 0.2 }}
              >
                <Icon 
                  size={20} 
                  style={{ 
                    color: isActive ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.8)',
                    transition: 'color 0.2s ease',
                  }}
                />
              </motion.div>

              {/* Text - في المنتصف */}
              <span 
                className="fw-medium flex-grow-1"
                style={{ 
                  fontSize: '0.9375rem',
                  color: isActive ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.9)',
                  textAlign: 'right',
                  lineHeight: 1.5,
                  letterSpacing: '0',
                  transition: 'color 0.2s ease',
                  direction: 'rtl',
                  unicodeBidi: 'bidi-override',
                  order: 2,
                }}
              >
                {t(`common.${item.key}`)}
              </span>

              {/* Chevron - على اليمين */}
              <motion.div
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '16px',
                  height: '16px',
                  order: 3,
                }}
                animate={{
                  opacity: isActive ? 1 : 0.5,
                  x: isActive ? 0 : 4,
                }}
                transition={{ duration: 0.2 }}
              >
                <ChevronIcon 
                  size={14}
                  style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                  }}
                />
              </motion.div>
            </>
          ) : (
            <>
              {/* LTR Layout: Icon (left) → Text → Chevron (right) */}
              {/* Icon - على اليسار */}
              <motion.div
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                }}
                animate={{
                  scale: isActive ? 1.1 : 1,
                }}
                transition={{ duration: 0.2 }}
              >
                <Icon 
                  size={20} 
                  style={{ 
                    color: isActive ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.8)',
                    transition: 'color 0.2s ease',
                  }}
                />
              </motion.div>

              {/* Text and Chevron */}
              {!isCollapsed && (
                <>
                  {/* Text - في المنتصف */}
                  <span 
                    className="fw-medium flex-grow-1"
                    style={{ 
                      fontSize: '0.9375rem',
                      color: isActive ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.9)',
                      textAlign: 'left',
                      lineHeight: 1.5,
                      letterSpacing: '0.01em',
                      transition: 'color 0.2s ease',
                      direction: 'ltr',
                    }}
                  >
                    {t(`common.${item.key}`)}
                  </span>

                  {/* Chevron - على اليمين */}
                  <motion.div
                    style={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '16px',
                      height: '16px',
                    }}
                    animate={{
                      opacity: isActive ? 1 : 0.5,
                      x: isActive ? 0 : 4,
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronIcon 
                      size={14}
                      style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                      }}
                    />
                  </motion.div>
                </>
              )}
            </>
          )}

          {/* Tooltip for collapsed state */}
          {isCollapsed && (
            <motion.div
              className="position-absolute"
              style={{
                [isRTL ? 'left' : 'right']: 'calc(100% + 12px)',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(15, 23, 42, 0.95)',
                backdropFilter: 'blur(10px)',
                color: 'rgba(255, 255, 255, 0.95)',
                padding: '0.5rem 0.75rem',
                borderRadius: '8px',
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                opacity: 0,
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              {t(`common.${item.key}`)}
              <div
                className="position-absolute"
                style={{
                  [isRTL ? 'right' : 'left']: '-4px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderStyle: 'solid',
                  [isRTL 
                    ? 'borderRight' 
                    : 'borderLeft']: '4px solid rgba(15, 23, 42, 0.95)',
                  borderTop: '4px solid transparent',
                  borderBottom: '4px solid transparent',
                }}
              />
            </motion.div>
          )}
        </motion.div>
      </Link>
    </motion.li>
  );
};

// Sidebar Content Component
interface SidebarContentProps {
  isRTL: boolean;
  isCollapsed: boolean;
  t: (key: string) => string;
  location: { pathname: string };
  onNavigate: () => void;
}

const SidebarContent: React.FC<SidebarContentProps> = ({ 
  isRTL, 
  isCollapsed, 
  t, 
  location, 
  onNavigate 
}) => {
  return (
    <>
      {/* Navigation */}
      <nav 
        style={{ 
          padding: isCollapsed ? '1rem 0.5rem' : '1rem',
          paddingInlineStart: isCollapsed ? '0.5rem' : '1rem',
          paddingInlineEnd: isCollapsed ? '0.5rem' : '1rem',
          overflowY: 'auto',
          overflowX: 'hidden',
          height: 'calc(100vh - 80px)',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
        }}
        aria-label="Main navigation"
      >
        <ul 
          className="list-unstyled mb-0"
          style={{
            padding: 0,
            margin: 0,
          }}
        >
          {menuItems.map((item, index) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/' && location.pathname.startsWith(item.path));

            return (
              <SidebarItem
                key={item.path}
                item={item}
                isActive={isActive}
                isCollapsed={isCollapsed}
                isRTL={isRTL}
                t={t}
                index={index}
                onNavigate={onNavigate}
              />
            );
          })}
        </ul>
      </nav>

      {/* Footer Decoration */}
      <motion.div
        className="position-absolute bottom-0 start-0 end-0"
        style={{
          height: '120px',
          background: isRTL 
            ? 'linear-gradient(to top right, rgba(96, 165, 250, 0.15), transparent)'
            : 'linear-gradient(to top left, rgba(96, 165, 250, 0.15), transparent)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      />
    </>
  );
};

// Main Sidebar Component
export const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed } = useUiStore();
  const { isRTL } = useLanguageStore();
  const [isCollapsed, setIsCollapsed] = useState(sidebarCollapsed);

  // Sync local state with store
  useEffect(() => {
    setIsCollapsed(sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Update store when collapsed state changes
  const handleToggleCollapsed = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    setSidebarCollapsed(newCollapsed);
  };

  // Handle navigation (close mobile sidebar)
  const handleNavigate = () => {
    if (window.innerWidth < 992) {
      setSidebarOpen(false);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Close sidebar on Escape
      if (e.key === 'Escape' && sidebarOpen && window.innerWidth < 992) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen, setSidebarOpen]);

  const sidebarWidth = isCollapsed ? '80px' : '280px';

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ 
              zIndex: 1040, 
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
            }}
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <motion.aside
        className="position-fixed top-0 h-100 d-none d-lg-block"
        style={{
          [isRTL ? 'right' : 'left']: 0,
          width: sidebarWidth,
          zIndex: 1040,
          background: isRTL
            ? 'linear-gradient(to left, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 50%, rgba(15, 23, 42, 0.98) 100%)'
            : 'linear-gradient(to right, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 50%, rgba(15, 23, 42, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          borderInlineEnd: isRTL ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
          borderInlineStart: isRTL ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
          boxShadow: isRTL
            ? '-4px 0 24px rgba(0, 0, 0, 0.2)'
            : '4px 0 24px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
        }}
        dir={isRTL ? 'rtl' : 'ltr'}
        animate={{
          width: sidebarWidth,
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30,
        }}
        aria-label="Sidebar navigation"
      >
        <div className="position-relative h-100 d-flex flex-column">
          <SidebarHeader 
            isCollapsed={isCollapsed} 
            isRTL={isRTL} 
            t={t}
          />
          <SidebarContent 
            isRTL={isRTL} 
            isCollapsed={isCollapsed} 
            t={t} 
            location={location}
            onNavigate={handleNavigate}
          />
        </div>
        
        {/* Toggle Button - على الحافة الخارجية */}
        <motion.button
          onClick={handleToggleCollapsed}
          className="position-absolute"
          style={{
            top: '1rem',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            border: 'none',
            background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.9), rgba(59, 130, 246, 0.9))',
            backdropFilter: 'blur(10px)',
            color: 'rgba(255, 255, 255, 1)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1051,
            boxShadow: '0 4px 12px rgba(96, 165, 250, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          animate={{
            [isRTL ? 'right' : 'left']: isCollapsed ? '0' : '-16px',
          }}
          whileHover={{ 
            background: 'linear-gradient(135deg, rgba(96, 165, 250, 1), rgba(59, 130, 246, 1))',
            scale: 1.1,
            boxShadow: '0 6px 16px rgba(96, 165, 250, 0.5), 0 4px 8px rgba(0, 0, 0, 0.3)',
          }}
          whileTap={{ scale: 0.95 }}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <motion.div
            animate={{ 
              rotate: isCollapsed ? 0 : (isRTL ? 180 : -180)
            }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {isCollapsed 
              ? (isRTL ? <ChevronRight size={18} /> : <ChevronLeft size={18} />)
              : (isRTL ? <ChevronLeft size={18} /> : <ChevronRight size={18} />)
            }
          </motion.div>
        </motion.button>
      </motion.aside>

      {/* Mobile Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          x: sidebarOpen ? 0 : (isRTL ? '100%' : '-100%'),
        }}
        transition={{ 
          type: 'spring', 
          damping: 25, 
          stiffness: 200 
        }}
        className="position-fixed top-0 h-100 d-lg-none"
        style={{
          [isRTL ? 'right' : 'left']: 0,
          width: '280px',
          zIndex: 1050,
          background: isRTL
            ? 'linear-gradient(to left, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 50%, rgba(15, 23, 42, 0.98) 100%)'
            : 'linear-gradient(to right, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 50%, rgba(15, 23, 42, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          borderInlineEnd: isRTL ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
          borderInlineStart: isRTL ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
          boxShadow: isRTL
            ? '-4px 0 24px rgba(0, 0, 0, 0.3)'
            : '4px 0 24px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
        }}
        dir={isRTL ? 'rtl' : 'ltr'}
        aria-label="Mobile sidebar navigation"
      >
        <div className="position-relative h-100 d-flex flex-column">
          <div 
            className="d-flex align-items-center"
            style={{
              padding: '1.5rem',
              paddingBlockEnd: '1.25rem',
              paddingInlineStart: '1.5rem',
              paddingInlineEnd: '1.5rem',
              borderBlockEnd: '1px solid rgba(255, 255, 255, 0.1)',
              minHeight: '80px',
              justifyContent: 'space-between',
              flexDirection: 'row',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <SidebarHeader isCollapsed={false} isRTL={isRTL} t={t} />
            </div>
            <motion.button
              onClick={() => setSidebarOpen(false)}
              className="btn btn-link p-2"
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                flexShrink: 0,
                marginInlineStart: isRTL ? '0' : '0.5rem',
                marginInlineEnd: isRTL ? '0.5rem' : '0',
              }}
              whileHover={{ scale: 1.1, color: '#fff' }}
              whileTap={{ scale: 0.9 }}
              aria-label="Close sidebar"
            >
              <X size={20} />
            </motion.button>
          </div>
          <SidebarContent 
            isRTL={isRTL} 
            isCollapsed={false} 
            t={t} 
            location={location}
            onNavigate={handleNavigate}
          />
        </div>
      </motion.aside>
    </>
  );
};
